import React, { useEffect, useRef, useState } from 'react';
import { Trophy, Medal, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { gamificationAPI } from '../services/api';

const PAGE_SIZE = 30;
const AUTO_REFRESH_INTERVAL_MS = 60_000;

const formatTime = (date) =>
  date
    ? date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '--:--:--';

const currentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { start: fmt(start), end: fmt(end) };
};

const medalColor = (position) => {
  if (position === 0) return 'text-amber-300';
  if (position === 1) return 'text-slate-300';
  if (position === 2) return 'text-orange-400';
  return 'text-slate-500';
};

const LoadingRows = () => (
  <div className="space-y-2">
    {[...Array(6)].map((_, index) => (
      <div key={`game-ranking-skeleton-${index}`} className="grid grid-cols-12 gap-3 items-center rounded-lg border border-gray-800 bg-gray-900 px-4 py-3">
        <Skeleton className="h-4 col-span-1 bg-gray-800" />
        <Skeleton className="h-4 col-span-7 bg-gray-800" />
        <Skeleton className="h-4 col-span-4 bg-gray-800" />
      </div>
    ))}
  </div>
);

const AdminGameRankingPage = () => {
  const defaultRange = currentMonthRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [nameInput, setNameInput] = useState('');
  const [name, setName] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const paramsRef = useRef({ startDate, endDate, name, page });
  paramsRef.current = { startDate, endDate, name, page };

  const requestId = useRef(0);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(1);
      setName(nameInput.trim());
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [nameInput]);

  const load = async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    const currentRequest = ++requestId.current;
    try {
      const { startDate: start, endDate: end, name: currentName, page: currentPage } = paramsRef.current;
      const response = await gamificationAPI.getGameRanking({
        start,
        end,
        name: currentName || undefined,
        page: currentPage,
        pageSize: PAGE_SIZE,
      });
      if (currentRequest !== requestId.current) return;
      setRows(response.data?.items || []);
      setTotal(response.data?.total || 0);
      setLastUpdated(new Date());
    } catch (error) {
      if (currentRequest !== requestId.current) return;
      if (!silent) {
        setRows([]);
        setTotal(0);
        toast.error(error?.response?.data?.detail || 'Não foi possível carregar o placar.');
      }
    } finally {
      if (currentRequest === requestId.current) {
        setRefreshing(false);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, name, page]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      load({ silent: true });
    }, AUTO_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRangeChange = (setter) => (event) => {
    setPage(1);
    setter(event.target.value);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStartIndex = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEndIndex = Math.min(page * PAGE_SIZE, total);

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-game-ranking-page">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white">Game Ranking</h1>
          <p className="text-gray-400 mt-1">Placar de pontos da gamificação, por período — soma direta dos pontos, sem pesos.</p>
        </div>

        <Card className="border-gray-800 bg-gray-900">
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Trophy size={18} className="text-amber-300" />
              Placar
              <span className="ml-2 flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-950/40 px-2 py-0.5 text-xs font-normal text-emerald-300">
                <span className="relative flex h-2 w-2">
                  <span
                    className={`absolute inline-flex h-full w-full rounded-full bg-emerald-400 ${
                      refreshing ? 'animate-ping opacity-75' : 'opacity-0'
                    }`}
                  />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                </span>
                Ao vivo
              </span>
              {lastUpdated && (
                <span className="text-xs font-normal text-gray-500">
                  Atualizado às {formatTime(lastUpdated)}
                </span>
              )}
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                placeholder="Filtrar por nome"
                value={nameInput}
                onChange={(event) => setNameInput(event.target.value)}
                className="w-48 bg-gray-950 border-gray-800 text-white"
                aria-label="Filtrar por nome do membro"
              />
              <Input
                type="date"
                value={startDate}
                onChange={handleRangeChange(setStartDate)}
                className="w-40 bg-gray-950 border-gray-800 text-white"
                aria-label="Data inicial do período"
              />
              <span className="text-gray-500 text-sm">até</span>
              <Input
                type="date"
                value={endDate}
                onChange={handleRangeChange(setEndDate)}
                className="w-40 bg-gray-950 border-gray-800 text-white"
                aria-label="Data final do período"
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-gray-400">Inclui as duas datas. Relatos pela data do convite; eventos pela data do lançamento.</p>
            {loading ? (
              <LoadingRows />
            ) : rows.length === 0 ? (
              <div className="rounded-lg border border-gray-800 bg-gray-950 px-4 py-6 text-center text-gray-300">
                {name ? 'Nenhum membro encontrado com esse nome no período.' : 'Nenhum ponto registrado neste período.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-sm">
                  <thead>
                    <tr className="border-b border-gray-800 text-gray-400 text-left">
                      <th className="px-3 py-3 font-medium w-14">#</th>
                      <th className="px-3 py-3 font-medium">Membro</th>
                      <th className="px-3 py-3 font-medium">Relatos</th>
                      <th className="px-3 py-3 font-medium">Eventos</th>
                      <th className="px-3 py-3 font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => {
                      const position = (page - 1) * PAGE_SIZE + index;
                      return (
                        <tr key={row.user_id} className="border-b border-gray-800/80 text-gray-200">
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-1">
                              {position < 3 ? <Medal size={16} className={medalColor(position)} /> : null}
                              <span className="font-medium text-white">{position + 1}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 font-medium text-white">{row.full_name}</td>
                          <td className="px-3 py-3">{row.member_points ?? 0}</td>
                          <td className="px-3 py-3">{row.event_points ?? 0}</td>
                          <td className="px-3 py-3">
                            <Badge className="border border-indigo-400/30 bg-indigo-950/70 text-indigo-200">
                              {row.total_points} pts
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {total > 0 && (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pt-2">
                <p className="text-xs text-gray-500">
                  Mostrando {rangeStartIndex}–{rangeEndIndex} de {total} membro{total === 1 ? '' : 's'}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-gray-800 text-gray-300 hover:text-white"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || loading}
                  >
                    <ChevronLeft size={14} className="mr-1" />
                    Anterior
                  </Button>
                  <span className="text-xs text-gray-400 px-1">
                    Página {page} de {totalPages}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-gray-800 text-gray-300 hover:text-white"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages || loading}
                  >
                    Próxima
                    <ChevronRight size={14} className="ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminGameRankingPage;
